import type { CreateEntityDto } from "@newaydata/logbook-core";
import type {
  Aircraft,
  BoardBook as FwBoardBook,
  CrewMember,
  Leg as FwLeg,
  Person,
  Place,
} from "@newaydata/logbook-schemas/generated/typescript/logbook";
import type { BookHeader, CrewEntry, LogEntry } from "./boardbook-model";

/**
 * Pure mapping between the flat board-book **view model** (`boardbook-model.ts`,
 * what the page binds) and the framework's normalized FCL.050 entities
 * (Aircraft / Place / Person / CrewMember / BoardBook / Leg). FK ids are
 * resolved by the service at write time (upsert), so the DTO builders take the
 * resolved ids as arguments.
 *
 * A boardbook = one BoardBook + a header + many Legs (`LogEntry`), each with
 * its own crew rows.
 */

export * from "./boardbook-model";

/** Assemble a flat `BoardBook` view from a registration + header + entries. */
export function bookView(
  registration: string,
  header: BookHeader,
  entries: LogEntry[],
  updatedAt: number
): import("./boardbook-model").BoardBook {
  return { registration, header, entries, updatedAt };
}

/** `Leg.flight_rule` is required but the view model may carry a stray value — default to VFR. */
export const DEFAULT_FLIGHT_RULE = "VFR" as const;

export function aircraftDto(registration: string): CreateEntityDto<Aircraft> {
  return { registration };
}

export function placeDto(icao: string, name?: string): CreateEntityDto<Place> {
  return name?.trim() ? { icao, name: name.trim() } : { icao };
}

/** A crew row's identity fields become the Person's name (+ external id when picked from a roster). */
export function personDto(crew: CrewEntry): CreateEntityDto<Person> {
  const dto: CreateEntityDto<Person> = { family_name: crew.familyName };
  if (crew.givenNames) dto.given_names = crew.givenNames;
  if (crew.externalId) dto.external_id = crew.externalId;
  return dto;
}

/** Find-or-create filter for a crew row's Person — external id when the row came from a roster, else by name.
 * A free-text row never matches a roster Person, because a roster Person carries an external id. */
export function personFilter(crew: CrewEntry): Record<string, string | null> {
  if (crew.externalId) return { external_id: crew.externalId };
  const filter: Record<string, string | null> = { family_name: crew.familyName, external_id: null };
  if (crew.givenNames) filter.given_names = crew.givenNames;
  return filter;
}

/** Set a key only when the value is neither `null` nor `""`. */
function setIfPresent<T extends object, K extends keyof T>(dto: T, key: K, value: T[K] | null | ""): void {
  if (value !== null && value !== "") (dto as Record<string, unknown>)[key as string] = value;
}

/** Set a key to the value, or to `null` when it is `null` or `""`. An update is a patch,
 * so a cleared column must be named with `null`, else it keeps its old value. */
function setOrNull<T extends object, K extends keyof T>(dto: T, key: K, value: T[K] | null | ""): void {
  (dto as Record<string, unknown>)[key as string] = value === "" ? null : value;
}

export function legDto(
  entry: LogEntry,
  departurePlaceId: string,
  arrivalPlaceId: string,
  boardBookId: string
): CreateEntityDto<FwLeg> {
  const rollDate = (hhmm: string): string =>
    crossesMidnight(entry.blockOff, hhmm) ? addOneDay(entry.date) : entry.date;

  const dto: CreateEntityDto<FwLeg> = {
    departure_place: departurePlaceId,
    arrival_place: arrivalPlaceId,
    board_book_id: boardBookId,
    time_out: toIsoDateTime(entry.date, entry.blockOff),
    time_in: toIsoDateTime(rollDate(entry.blockOn), entry.blockOn),
    flight_rule: entry.flightRule === "VFR" || entry.flightRule === "IFR" ? entry.flightRule : DEFAULT_FLIGHT_RULE,
    landings_count: coerceCount(entry.landings),
    external_id: entry.id,
    external_updated_at: entry.updatedAt,
  };

  if (/^\d{2}:\d{2}$/.test(entry.takeoff)) dto.time_off = toIsoDateTime(rollDate(entry.takeoff), entry.takeoff);
  if (/^\d{2}:\d{2}$/.test(entry.landing)) dto.time_on = toIsoDateTime(rollDate(entry.landing), entry.landing);

  setIfPresent(dto, "flight_purpose", entry.flightPurpose);
  setIfPresent(dto, "approach_type", entry.approachType);
  setIfPresent(dto, "flight_number", entry.flightNumber);
  setIfPresent(dto, "fuel_upl_before", entry.fuelUplBefore);
  setIfPresent(dto, "fuel_at_takeoff", entry.fuelAtTakeoff);
  setIfPresent(dto, "fuel_upl_after", entry.fuelUplAfter);
  setIfPresent(dto, "fuel_at_landing", entry.fuelAtLanding);
  setIfPresent(dto, "passenger_count", entry.passengers);
  setIfPresent(dto, "mission_type", entry.missionType);
  setIfPresent(dto, "mission_subtype", entry.missionSubtype);
  setIfPresent(dto, "engine_1_n1", entry.engine1N1);
  setIfPresent(dto, "engine_1_n2", entry.engine1N2);
  setIfPresent(dto, "engine_1_cdv", entry.engine1Cdv);
  setIfPresent(dto, "engine_2_n1", entry.engine2N1);
  setIfPresent(dto, "engine_2_n2", entry.engine2N2);
  setIfPresent(dto, "engine_2_cdv", entry.engine2Cdv);
  setIfPresent(dto, "winch_cycles", entry.winchCycles);
  setIfPresent(dto, "winch_motor_hours", entry.winchMotorHours);
  setIfPresent(dto, "delivery_note", entry.deliveryNote);
  setIfPresent(dto, "internal_ref", entry.internalRef);
  setIfPresent(dto, "remarks", entry.remarks);
  setIfPresent(dto, "locked_at", entry.lockedAt);
  if (entry.catA) dto.is_cat_a_operation = true;

  return dto;
}

export function crewDto(crew: CrewEntry, personId: string, legId: string): CreateEntityDto<CrewMember> {
  const dto: CreateEntityDto<CrewMember> = {
    role: crew.role,
    person: personId,
    leg_id: legId,
  };

  if (crew.seat) dto.seat = crew.seat;
  setIfPresent(dto, "block_time", crew.blockTime);
  setIfPresent(dto, "flight_time", crew.flightTime);
  setIfPresent(dto, "takeoffs_day", crew.takeoffsDay);
  setIfPresent(dto, "takeoffs_night", crew.takeoffsNight);
  setIfPresent(dto, "landings_day", crew.landingsDay);
  setIfPresent(dto, "landings_night", crew.landingsNight);
  setIfPresent(dto, "ifr_approaches", crew.ifrApproaches);
  setIfPresent(dto, "night_time", crew.nightTime);
  setIfPresent(dto, "ifr_time", crew.ifrTime);
  setIfPresent(dto, "hoist_day", crew.hoistDay);
  setIfPresent(dto, "hoist_night", crew.hoistNight);
  setIfPresent(dto, "hoist_stretcher", crew.hoistStretcher);
  setIfPresent(dto, "hoist_double_pax", crew.hoistDoublePax);
  setIfPresent(dto, "hoist_single_pax", crew.hoistSinglePax);
  if (crew.checkFlight) dto.check_flight = true;
  if (crew.nvis) dto.nvis_flag = true;
  if (crew.nvisCheckFlight) dto.nvis_check_flight = true;

  return dto;
}

/** The BoardBook row. Unlike the leg and crew DTOs, every optional column is written,
 * as `null` when blank: the book row is updated in place, and an update is a patch. */
export function boardBookDto(
  aircraftId: string,
  header: BookHeader,
  fallbackDateOpened: string
): CreateEntityDto<FwBoardBook> {
  const dto: CreateEntityDto<FwBoardBook> = {
    date_opened: header.dateOpened || fallbackDateOpened,
    aircraft: aircraftId,
  };

  setOrNull(dto, "date_closed", header.dateClosed);
  setOrNull(dto, "fuel_unit", header.fuelUnit);
  setOrNull(dto, "carried_forward_flight_time", header.cfFlightTime);
  setOrNull(dto, "carried_forward_block_time", header.cfBlockTime);
  setOrNull(dto, "carried_forward_landings", header.cfLandings);
  setOrNull(dto, "carried_forward_engine_1_n1", header.cfEngine1N1);
  setOrNull(dto, "carried_forward_engine_1_n2", header.cfEngine1N2);
  setOrNull(dto, "carried_forward_engine_1_cdv", header.cfEngine1Cdv);
  setOrNull(dto, "carried_forward_engine_2_n1", header.cfEngine2N1);
  setOrNull(dto, "carried_forward_engine_2_n2", header.cfEngine2N2);
  setOrNull(dto, "carried_forward_engine_2_cdv", header.cfEngine2Cdv);
  setOrNull(dto, "carried_forward_winch_hours", header.cfWinchHours);

  return dto;
}

/** The inverse of `boardBookDto` — projects a normalized row back into a flat `BookHeader`. */
export function headerFromRow(bb: FwBoardBook | undefined, updatedAt: number): BookHeader {
  return {
    updatedAt,
    dateOpened: bb?.date_opened ?? "",
    dateClosed: bb?.date_closed ?? "",
    fuelUnit: bb?.fuel_unit ?? "",
    cfFlightTime: bb?.carried_forward_flight_time ?? null,
    cfBlockTime: bb?.carried_forward_block_time ?? null,
    cfLandings: bb?.carried_forward_landings ?? null,
    cfEngine1N1: bb?.carried_forward_engine_1_n1 ?? null,
    cfEngine1N2: bb?.carried_forward_engine_1_n2 ?? null,
    cfEngine1Cdv: bb?.carried_forward_engine_1_cdv ?? null,
    cfEngine2N1: bb?.carried_forward_engine_2_n1 ?? null,
    cfEngine2N2: bb?.carried_forward_engine_2_n2 ?? null,
    cfEngine2Cdv: bb?.carried_forward_engine_2_cdv ?? null,
    cfWinchHours: bb?.carried_forward_winch_hours ?? null,
  };
}

/** The inverse of `crewDto` + `personDto` — projects a normalized crew row (+ its resolved Person) back into a flat `CrewEntry`. */
export function crewFromRow(member: CrewMember, person?: Person): CrewEntry {
  return {
    role: member.role,
    familyName: person?.family_name ?? "",
    givenNames: person?.given_names ?? "",
    externalId: person?.external_id ?? "",
    seat: member.seat ?? "",
    blockTime: member.block_time ?? null,
    flightTime: member.flight_time ?? null,
    takeoffsDay: member.takeoffs_day ?? null,
    takeoffsNight: member.takeoffs_night ?? null,
    landingsDay: member.landings_day ?? null,
    landingsNight: member.landings_night ?? null,
    ifrApproaches: member.ifr_approaches ?? null,
    checkFlight: member.check_flight === true,
    nightTime: member.night_time ?? null,
    ifrTime: member.ifr_time ?? null,
    hoistDay: member.hoist_day ?? null,
    hoistNight: member.hoist_night ?? null,
    hoistStretcher: member.hoist_stretcher ?? null,
    hoistDoublePax: member.hoist_double_pax ?? null,
    hoistSinglePax: member.hoist_single_pax ?? null,
    nvis: member.nvis_flag === true,
    nvisCheckFlight: member.nvis_check_flight === true,
  };
}

/** Everything the service has read back for a single leg, to project into a flat `LogEntry`. */
export interface LogEntrySources {
  leg?: FwLeg;
  departurePlace?: Place;
  arrivalPlace?: Place;
  crew: { member: CrewMember; person?: Person }[];
}

/** The inverse of `legDto` for a single leg — projects a normalized leg + its
 * resolved FK rows back into one flat `LogEntry`. Sync identity is read from
 * `external_id`/`external_updated_at` when present (post-migration leg); a
 * leg with no `external_id` falls back to the LEGACY reading, where the sync
 * id lived in `internal_ref` and the epoch-ms stamp in `delivery_note`. A leg
 * with neither gets a **deterministic** `contentId`, so the same flown leg on
 * two devices derives the SAME id and dedups by per-entry LWW instead of
 * duplicating. */
export function entryFromRows(s: LogEntrySources): LogEntry {
  const leg = s.leg;

  let id = "";
  let updatedAt = 0;
  let internalRef = leg?.internal_ref ?? "";
  let deliveryNote = leg?.delivery_note ?? "";

  if (leg?.external_id) {
    id = leg.external_id;
    updatedAt = leg.external_updated_at ?? 0;
  } else {
    const rawUpdatedAt = Number(leg?.delivery_note);
    id = leg?.internal_ref || "";
    updatedAt = Number.isFinite(rawUpdatedAt) && rawUpdatedAt >= 0 ? rawUpdatedAt : 0;
    internalRef = "";
    deliveryNote = "";
  }

  const e: LogEntry = {
    id,
    updatedAt,
    lockedAt: leg?.locked_at ?? "",
    date: dateFromIso(leg?.time_out),
    departure: s.departurePlace?.icao ?? "",
    departureName: s.departurePlace?.name ?? "",
    destination: s.arrivalPlace?.icao ?? "",
    destinationName: s.arrivalPlace?.name ?? "",
    flightNumber: leg?.flight_number ?? "",
    flightRule: leg?.flight_rule ?? "VFR",
    flightPurpose: leg?.flight_purpose ?? "",
    approachType: leg?.approach_type ?? "",
    blockOff: hhmmFromIso(leg?.time_out),
    takeoff: hhmmFromIso(leg?.time_off),
    landing: hhmmFromIso(leg?.time_on),
    blockOn: hhmmFromIso(leg?.time_in),
    landings: leg?.landings_count ?? 0,
    fuelUplBefore: leg?.fuel_upl_before ?? null,
    fuelAtTakeoff: leg?.fuel_at_takeoff ?? null,
    fuelUplAfter: leg?.fuel_upl_after ?? null,
    fuelAtLanding: leg?.fuel_at_landing ?? null,
    passengers: leg?.passenger_count ?? null,
    missionType: leg?.mission_type ?? "",
    missionSubtype: leg?.mission_subtype ?? "",
    catA: leg?.is_cat_a_operation === true,
    engine1N1: leg?.engine_1_n1 ?? null,
    engine1N2: leg?.engine_1_n2 ?? null,
    engine1Cdv: leg?.engine_1_cdv ?? null,
    engine2N1: leg?.engine_2_n1 ?? null,
    engine2N2: leg?.engine_2_n2 ?? null,
    engine2Cdv: leg?.engine_2_cdv ?? null,
    winchCycles: leg?.winch_cycles ?? null,
    winchMotorHours: leg?.winch_motor_hours ?? null,
    deliveryNote,
    internalRef,
    remarks: leg?.remarks ?? "",
    crew: s.crew.map((row) => crewFromRow(row.member, row.person)),
  };

  return { ...e, id: id || contentId(e) };
}

/** A deterministic sync id derived from an entry's full content — the fallback
 * identity for legacy/pre-id entries (both on a local DB read and at the remote
 * trust boundary). Includes EVERY distinguishing field (landings + remarks too),
 * so two genuinely different legs never collide and two copies of the same flown
 * leg always match. Real (post-id) entries carry a UUID and never use this.
 * Uses the first PIC crew row's family name — matches the legacy single-captain
 * format byte-for-byte, so old deterministic ids stay stable. */
export function contentId(e: LogEntry): string {
  const pic = e.crew.find((c) => c.role === "PIC")?.familyName ?? "";
  return `c:${e.date}|${pic}|${e.departure}|${e.destination}|${e.blockOff}|${e.blockOn}|${e.landings}|${e.remarks}`;
}

/** Landings is a free-form number input — clamp to a non-negative integer. */
export function coerceCount(n: unknown): number {
  const v = Math.floor(Number(n));
  return Number.isFinite(v) && v > 0 ? v : 0;
}

/** Combine an ISO date (`yyyy-mm-dd`) + `HH:mm` into an RFC3339 UTC datetime. */
export function toIsoDateTime(date: string, hhmm: string): string {
  const d = date || "1970-01-01";
  const t = /^\d{2}:\d{2}$/.test(hhmm) ? hhmm : "00:00";
  return `${d}T${t}:00Z`;
}

/** Date portion (`yyyy-mm-dd`) of an RFC3339 datetime. The `1970-01-01` sentinel
 * that `toIsoDateTime` writes for a blank date maps back to "" — else an entry
 * saved with no date round-trips as `1970-01-01`. */
export function dateFromIso(iso: string | undefined): string {
  const d = (iso ?? "").slice(0, 10);
  return d === "1970-01-01" ? "" : d;
}

/** Extract `HH:mm` from an RFC3339 datetime; "" when absent/unparseable. */
export function hhmmFromIso(iso: string | undefined): string {
  const m = iso ? /T(\d{2}:\d{2})/.exec(iso) : null;
  return m ? m[1] : "";
}

/** True when both times are set and the second `HH:mm` is before the first (next-day roll). */
export function crossesMidnight(blockOff: string, other: string): boolean {
  return /^\d{2}:\d{2}$/.test(blockOff) && /^\d{2}:\d{2}$/.test(other) && other < blockOff;
}

/** Add one calendar day to a `yyyy-mm-dd` date string (UTC); passthrough on a malformed date. */
export function addOneDay(date: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
